/**
 * The mail composer's body, WYSIWYG like Spark and Gmail: bold, italic, strike, code and links show as styled text
 * while the draft stays the composer's markdown (core logic/mailRichText converts both ways), so drafts, templates,
 * Ask AI and sending are unchanged. Built on RichTextField (ProseMirror, loaded lazily; the plain field shows the
 * markdown until it loads). Enter breaks the line and continues a `- ` / `1. ` list; ⌘B / ⌘I / ⌘⇧X / ⌘E toggle marks
 * (at a caret, for what's typed next); ⌘⇧U or the toolbar's link opens the address row; a pasted address over a
 * selection links it.
 */
import { MailRichText, type FormatKind, type MailMark, type MailMarks, type MailRun } from "@bundle/core";
import { applyFormat } from "@bundle/core/logic/composerFormat";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n/I18nProvider.tsx";
import { Button, IconButton, Stack, TextField } from "../primitives/index.ts";
import { preloadRichTextField, RichTextField, type RichRun, type RichTextKeyEvent } from "../primitives/RichTextField.tsx";

/** What the composer's toolbar and emoji picker call on the body. */
export type MailBodyHandle = {
  format: (kind: FormatKind) => void;
  insert: (text: string) => void;
};

export type MailBodyFieldProps = {
  value: string;
  onChange: (markdown: string) => void;
  placeholder: string;
  accessibilityLabel?: string;
  autoFocus?: boolean;
  /** Put the caret at `offset` of the visible text whenever `seq` changes. */
  focus?: { offset: number; seq: number };
  handleRef?: { current: MailBodyHandle | null };
  testID: string;
};

const UNDO_COALESCE_MS = 1000;
const UNDO_MAX = 100;
const URL_RE = /^(?:https?:\/\/|mailto:)\S+$/i;
const LIST_RE = /^(\s*)(?:([-*•])|(\d{1,9})([.)]))\s+(.*)$/;

type Doc = { md: string; runs: MailRun[] };
type Snapshot = { runs: MailRun[]; caret: number };

const trimEnd = (s: string) => s.replace(/\s+$/u, "");
const richRuns = (runs: MailRun[]): RichRun[] => runs.map((r) => ({ ...r }));

export function MailBodyField(p: MailBodyFieldProps) {
  const t = useT();
  useEffect(preloadRichTextField, []);
  const [own, setOwn] = useState<Doc>(() => ({
    md: p.value,
    runs: MailRichText.runsOfMarkdown(p.value),
  }));
  // A value from outside (a template, Ask AI, the draft loading) replaces what's shown. The draft drops trailing
  // whitespace, so a line just started isn't one.
  const runs = useMemo(() => (trimEnd(own.md) === trimEnd(p.value) ? own.runs : MailRichText.runsOfMarkdown(p.value)), [own, p.value]);
  const sel = useRef({ start: 0, end: 0 });
  /** Marks toggled at a caret, for what's typed there next (⌘B then typing). */
  const pending = useRef<{ at: number; marks: MailMarks } | null>(null);
  const undo = useRef<{ past: Snapshot[]; future: Snapshot[]; at: number }>({
    past: [],
    future: [],
    at: 0,
  });
  const [focus, setFocus] = useState<{ offset: number; end?: number; seq: number } | undefined>(undefined);
  const [link, setLink] = useState<{
    start: number;
    end: number;
    url: string;
  } | null>(null);
  const latest = useRef({ runs, p });
  useLayoutEffect(() => {
    latest.current = { runs, p };
  });

  const commit = (next: MailRun[], caret?: { start: number; end: number }, record = true, typing = false) => {
    const cur = latest.current;
    const h = undo.current;
    const now = Date.now();
    if (record && !(typing && now - h.at < UNDO_COALESCE_MS && h.past.length > 0)) {
      h.past = [...h.past, { runs: cur.runs, caret: sel.current.end }].slice(-UNDO_MAX);
      h.future = [];
    }
    h.at = typing ? now : 0;
    const md = MailRichText.markdownOfRuns(next);
    setOwn({ md, runs: next });
    if (caret !== undefined) {
      sel.current = caret;
      setFocus((f) => ({
        offset: caret.start,
        ...(caret.end === caret.start ? {} : { end: caret.end }),
        seq: (f?.seq ?? 0) + 1,
      }));
    }
    if (trimEnd(md) !== trimEnd(cur.p.value)) cur.p.onChange(md);
  };

  const replace = (start: number, end: number, inserted: string, caret?: boolean) => {
    const cur = latest.current;
    const pend = pending.current;
    const marks = pend !== null && pend.at === start && start === end ? pend.marks : undefined;
    pending.current = null;
    const next = MailRichText.replaceText(cur.runs, start, end, inserted, marks);
    const at = start + inserted.length;
    const typing = start === end && inserted.length === 1 && !/\s/u.test(inserted);
    commit(next, caret === true || marks !== undefined ? { start: at, end: at } : undefined, true, typing);
  };

  const toggle = (mark: MailMark) => {
    const cur = latest.current;
    const { start, end } = sel.current;
    if (start === end) {
      const base = pending.current?.at === start ? pending.current.marks : MailRichText.marksAt(cur.runs, start, end);
      pending.current = {
        at: start,
        marks: { ...base, [mark]: base[mark] !== true },
      };
      // A toolbar button took the focus: give it back where typing continues.
      setFocus((f) => ({ offset: start, seq: (f?.seq ?? 0) + 1 }));
      return;
    }
    commit(MailRichText.toggleMark(cur.runs, start, end, mark), { start, end });
  };

  /** List / quote / code block: the line syntax added or removed (logic/composerFormat), marks kept elsewhere. */
  const lines = (kind: FormatKind) => {
    const cur = latest.current;
    const plain = MailRichText.plainText(cur.runs);
    const { start, end } = sel.current;
    const e = applyFormat(plain, start, end, kind);
    const before = plain.split("\n");
    const after = e.text.split("\n");
    let next = cur.runs;
    if (before.length === after.length) {
      // Each line only changed at its start: edit those starts, so the marks after them stay.
      let at = 0;
      before.forEach((line, i) => {
        const now = after[i]!;
        let tail = 0;
        while (tail < line.length && tail < now.length && line[line.length - 1 - tail] === now[now.length - 1 - tail]) tail++;
        if (line !== now) next = MailRichText.replaceText(next, at, at + line.length - tail, now.slice(0, now.length - tail), {});
        at += now.length + 1;
      });
    } else {
      let a = 0;
      while (a < plain.length && a < e.text.length && plain[a] === e.text[a]) a++;
      let b = 0;
      while (b < plain.length - a && b < e.text.length - a && plain[plain.length - 1 - b] === e.text[e.text.length - 1 - b]) b++;
      next = MailRichText.replaceText(next, a, plain.length - b, e.text.slice(a, e.text.length - b), {});
    }
    commit(next, { start: e.start, end: e.end });
  };

  const openLink = () => {
    const cur = latest.current;
    const { start, end } = sel.current;
    const at = MailRichText.linkAt(cur.runs, start, end);
    setLink(at ?? { start, end, url: "" });
  };

  const applyLink = (url: string | null) => {
    const l = link;
    setLink(null);
    if (l === null) return;
    const cur = latest.current;
    const u = url?.trim() ?? "";
    if (u === "" || url === null) {
      commit(MailRichText.setLink(cur.runs, l.start, l.end, null), {
        start: l.end,
        end: l.end,
      });
      return;
    }
    const href = /^(?:https?:|mailto:)/i.test(u) ? u : u.includes("@") && !u.includes("/") ? `mailto:${u}` : `https://${u}`;
    if (l.start === l.end) {
      // No text selected: the address itself becomes the linked text.
      const next = MailRichText.replaceText(cur.runs, l.start, l.end, u, {
        link: href,
      });
      commit(next, { start: l.start + u.length, end: l.start + u.length });
      return;
    }
    commit(MailRichText.setLink(cur.runs, l.start, l.end, href), {
      start: l.end,
      end: l.end,
    });
  };

  const format = (kind: FormatKind) => {
    switch (kind) {
      case "bold":
      case "italic":
      case "strike":
      case "code":
        toggle(kind);
        return;
      case "link":
        openLink();
        return;
      default:
        lines(kind);
    }
  };

  useEffect(() => {
    const ref = p.handleRef;
    if (ref === undefined) return undefined;
    ref.current = {
      format,
      insert: (text) => replace(sel.current.start, sel.current.end, text, true),
    };
    return () => {
      ref.current = null;
    };
  });

  const step = (from: "past" | "future") => {
    const h = undo.current;
    const target = h[from].at(-1);
    if (target === undefined) return;
    const here: Snapshot = {
      runs: latest.current.runs,
      caret: sel.current.end,
    };
    if (from === "past") {
      h.past = h.past.slice(0, -1);
      h.future = [...h.future, here];
    } else {
      h.future = h.future.slice(0, -1);
      h.past = [...h.past, here];
    }
    commit(target.runs, { start: target.caret, end: target.caret }, false);
  };

  /** Enter: a new line, continuing a list (an empty item ends it, as in Gmail). */
  const newline = (start: number, end: number) => {
    const plain = MailRichText.plainText(latest.current.runs);
    const lineStart = plain.lastIndexOf("\n", start - 1) + 1;
    const m = LIST_RE.exec(plain.slice(lineStart, start));
    if (m !== null && start === end) {
      const [, indent = "", bullet, num, sep] = m;
      const prefix = `${indent}${bullet ?? `${Number(num) + 1}${sep}`} `;
      if ((m[5] ?? "") === "" && plain.slice(start, plain.indexOf("\n", start) < 0 ? plain.length : plain.indexOf("\n", start)) === "") {
        replace(lineStart, start, "", true);
        return;
      }
      pending.current = null;
      const next = MailRichText.replaceText(latest.current.runs, start, end, `\n${prefix}`, {});
      const at = start + 1 + prefix.length;
      commit(next, { start: at, end: at });
      return;
    }
    replace(start, end, "\n", true);
  };

  const onKey = (e: RichTextKeyEvent): boolean => {
    switch (e.key) {
      case "enter":
      case "shiftEnter":
        newline(e.start, e.end);
        return true;
      case "undo":
      case "redo":
        step(e.key === "undo" ? "past" : "future");
        return true;
      case "bold":
      case "italic":
      case "strike":
      case "code":
        sel.current = { start: e.start, end: e.end };
        toggle(e.key);
        return true;
      case "link":
        // ⌘K is search, as in Slack; links are ⌘⇧U (onFormatKey) or the toolbar.
        return false;
      case "underline":
        // Mail markdown has no underline (it reads as a link in mail); keep the browser from adding one.
        return true;
      default:
        return false;
    }
  };

  // A request from the composer (the reply box focusing its body).
  const outer = p.focus;
  const fieldFocus = useMemo(() => {
    if (outer === undefined) return focus;
    if (focus === undefined) return outer;
    return {
      ...(outer.seq > focus.seq ? outer : focus),
      seq: outer.seq + focus.seq,
    };
  }, [outer, focus]);

  return (
    <Stack gap="2">
      <RichTextField
        runs={richRuns(runs)}
        onChange={(start, end, inserted) => replace(start, end, inserted)}
        onSelect={(start, end) => {
          sel.current = { start, end };
          if (pending.current !== null && pending.current.at !== start) pending.current = null;
        }}
        onKey={onKey}
        onFormatKey={(key, start, end) => {
          sel.current = { start, end };
          format(key);
          return true;
        }}
        onPaste={(text, start, end) => {
          const clean = text.replace(/\r\n?/g, "\n");
          if (start !== end && URL_RE.test(clean.trim())) {
            commit(MailRichText.setLink(latest.current.runs, start, end, clean.trim()), { start: end, end });
            return true;
          }
          replace(start, end, clean, true);
          return true;
        }}
        focus={fieldFocus}
        autoFocus={p.autoFocus}
        placeholder={p.placeholder}
        accessibilityLabel={p.accessibilityLabel ?? p.placeholder}
        fallback={
          <TextField variant="plain" multiline value={p.value} onChange={p.onChange} placeholder={p.placeholder} autoFocus={p.autoFocus} testID={p.testID} />
        }
        testID={p.testID}
      />
      {link === null ? null : (
        <Stack direction="horizontal" align="center" gap="2" testID={`${p.testID}-link`}>
          <Stack grow>
            <TextField
              value={link.url}
              onChange={(url) => setLink({ ...link, url })}
              onSubmit={() => applyLink(link.url)}
              onKey={(k) => {
                if (k !== "escape") return false;
                setLink(null);
                return true;
              }}
              placeholder={t("mail.compose.linkAddress")}
              accessibilityLabel={t("mail.compose.linkAddress")}
              keyboard="url"
              autoFocus
              testID={`${p.testID}-link-url`}
            />
          </Stack>
          <Button size="sm" label={t("mail.compose.linkApply")} onPress={() => applyLink(link.url)} testID={`${p.testID}-link-apply`} />
          <IconButton icon="close" accessibilityLabel={t("mail.compose.linkRemove")} onPress={() => applyLink(null)} testID={`${p.testID}-link-remove`} />
        </Stack>
      )}
    </Stack>
  );
}
